import { forwardRef } from 'react'
import { classNames } from 'util/classNames'

const getFormClasses = ({ size }: { size?: 'sm' | 'md' | 'lg' }) => {
  const base =
    'block w-full appearance-none border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-toasty-blue-500 focus:bg-white focus:outline-none focus:ring-toasty-blue-500'
  switch (size) {
    case 'sm':
      return base + ' text-xs rounded-md px-2 py-1'
    case 'md':
      return base + ' text-xs rounded-md px-2 py-1.5'
    case 'lg':
      return base + ' text-base rounded-md px-2 py-1.5'
    default:
      return base
  }
}
function Label({ id, children }) {
  return (
    <label
      htmlFor={id}
      className="mb-2 block text-sm font-medium text-gray-700"
    >
      {children}
    </label>
  )
}

interface TextFieldProps {
  id: string
  label?: string | React.ReactNode
  type?: string
  className?: string
  autoComplete?: string
  required?: boolean
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      label,
      type = 'text',
      disabled = false,
      className = '',
      placeholder = '',
      size = 'md',
      ...props
    }: TextFieldProps,
    ref
  ) => {
    return (
      <div className={className}>
        {label && <Label id={id}>{label}</Label>}
        <input
          id={id}
          ref={ref}
          type={type}
          {...props}
          className={classNames(getFormClasses({ size }), 'mt-2')}
          disabled={disabled}
          placeholder={
            type === 'password'
              ? '••••••••'
              : type === 'email'
              ? 'Enter your email'
              : placeholder
          }
        />
      </div>
    )
  }
)

interface SelectFieldProps {
  id: string
  label?: string
  className?: string
  children: React.ReactNode
  placeholder?: string
  required?: boolean
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  (
    {
      id,
      label,
      className = '',
      children,
      placeholder = 'Select an option',
      ...props
    }: SelectFieldProps,
    ref
  ) => {
    return (
      <div className={className}>
        {label && <Label id={id}>{label}</Label>}
        <select
          id={id}
          className={classNames(getFormClasses({ size: 'md' }), 'pr-8')}
          ref={ref}
          {...props}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {children}
        </select>
      </div>
    )
  }
)
